import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import type {
  MemphisAssetAtRisk,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/** Stable id for the hardcoded CNIC Hawaii tropical-storm inbox item. */
export const CNIC_HAWAII_THREAT_NOTIFICATION_ID =
  'demo-cnic-hawaii-tropical-storm';

export const CNIC_HAWAII_ASSETS_LAYER_ID = 'cnic-hawaii-assets';
export const CNIC_HAWAII_ASSETS_LAYER_TITLE = 'CNIC Hawaii installations';

const CNIC_HAWAII_THREAT_SENT_AT = '2024-08-25T16:00:00.000Z';

export const CNIC_HAWAII_THREAT_PROVENANCE =
  '6 CNIC installations to be impacted by Tropical Storm per NHC Central Pacific';

export const CNIC_HAWAII_THRESHOLD_DESCRIPTION =
  'Tropical storm category threshold exceeded — forecast cone contains CNIC Navy Region Hawaii installations';

export const CNIC_HAWAII_AUTOMATION_RULE_NAME =
  'CNIC Hawaii — tropical storm forecast cone';

export const CNIC_HAWAII_EVENT_SUMMARY =
  'A tropical storm is approaching the Hawaiian Islands from the southeast. Navy Region Hawaii / CNIC installations on Oahu and Kauai sit inside the forecast cone and are expected to see tropical-storm-force winds, heavy rain, and harbor surge.';

export const CNIC_HAWAII_LINKED_EVENT_NAME =
  'Tropical Storm — 6 CNIC assets in Tropical Storm path';

export const CNIC_HAWAII_RESOURCES_COMMITTED =
  '6 installations are within the tropical storm cone';

export const CNIC_HAWAII_ORG_EVENT_DEMO_KEY = 'cnic-hawaii-cone';

export const CNIC_HAWAII_EVENT_STARTED_AT = '2024-08-25T16:00:00.000Z';

export const CNIC_HAWAII_RESOURCE_NOTES_PREFIX = 'cnic-hawaii:';

export const CNIC_HAWAII_SYMBOLOGY = 'facility';

export const CNIC_HAWAII_EVENT_REGION = 'Navy Region Hawaii';

export const CNIC_RESPONSE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'cnic-checklist-accountability',
    kind: 'send_notification',
    label: 'Send personnel accountability check',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate:
        'Accountability check: CNIC Hawaii installations in {eventName} cone',
      bodyTemplate:
        'Confirm personnel accountability for Navy Region Hawaii installations inside the {eventName} forecast cone. Report status in PRATUS.',
      emailSubject:
        'Accountability check: CNIC Hawaii installations — {eventName}',
      emailMessage:
        'Confirm personnel accountability for CNIC installations inside the {eventName} forecast cone. Review Courses of Action and assets at risk in PRATUS.',
      appMessage:
        'Accountability check: {eventName} — confirm personnel status for CNIC Hawaii installations.',
    },
  },
  {
    id: 'cnic-checklist-harbor-ops',
    kind: 'org_action',
    label: 'Assess harbor operations and ship movement',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'cnic-checklist-mission-essential',
    kind: 'org_action',
    label: 'Assess stability of mission essential functions',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const CNIC_RESPONSE_CHECKLIST_ORG_ACTION_LABELS =
  CNIC_RESPONSE_CHECKLIST_ITEMS.filter((item) => item.kind === 'org_action').map(
    (item) => item.label
  );

export function isCnicHawaiiOrgActionName(
  name: string | null | undefined
): boolean {
  return !!name && CNIC_RESPONSE_CHECKLIST_ORG_ACTION_LABELS.includes(name);
}

const TROPICAL_STORM_THREAT = 'Tropical Storm';

export function cnicChecklistsForThreats(
  threats: string[]
): Record<string, OrgEventChecklistItemDef[]> {
  const checklists: Record<string, OrgEventChecklistItemDef[]> = {};
  for (const threat of threats) {
    if (threat === TROPICAL_STORM_THREAT) {
      checklists[threat] = CNIC_RESPONSE_CHECKLIST_ITEMS;
    }
  }
  return checklists;
}

export function cnicChecklistsForAutomationRules(threats: string[]): {
  automationRules: string[];
  automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]>;
} {
  const threatChecklists = cnicChecklistsForThreats(threats);
  const automationRules: string[] = [];
  const automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]> =
    {};
  for (const threat of threats) {
    if (threat !== TROPICAL_STORM_THREAT) continue;
    if (!automationRules.includes(CNIC_HAWAII_AUTOMATION_RULE_NAME)) {
      automationRules.push(CNIC_HAWAII_AUTOMATION_RULE_NAME);
    }
    const items = threatChecklists[threat];
    if (items) automationRuleChecklists[CNIC_HAWAII_AUTOMATION_RULE_NAME] = items;
  }
  return { automationRules, automationRuleChecklists };
}

export type CnicHawaiiFacility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

export const CNIC_HAWAII_FACILITIES: CnicHawaiiFacility[] = [
  {
    id: 'cnic-jbphh',
    name: 'Joint Base Pearl Harbor-Hickam',
    city: 'Honolulu',
    state: 'HI',
    lon: -157.9439,
    lat: 21.3498,
  },
  {
    id: 'cnic-navy-region-hawaii-hq',
    name: 'Navy Region Hawaii HQ',
    city: 'Pearl Harbor',
    state: 'HI',
    lon: -157.9448,
    lat: 21.3547,
  },
  {
    id: 'cnic-navmag-west-loch',
    name: 'Naval Magazine Pearl Harbor (West Loch)',
    city: 'Ewa',
    state: 'HI',
    lon: -158.027,
    lat: 21.353,
  },
  {
    id: 'cnic-nctams-wahiawa',
    name: 'NCTAMS PAC Wahiawa',
    city: 'Wahiawa',
    state: 'HI',
    lon: -158.0194,
    lat: 21.5244,
  },
  {
    id: 'cnic-pmrf-barking-sands',
    name: 'Pacific Missile Range Facility Barking Sands',
    city: 'Kekaha',
    state: 'HI',
    lon: -159.785,
    lat: 22.0225,
  },
  {
    id: 'cnic-kalaeloa',
    name: 'Kalaeloa (former NAS Barbers Point)',
    city: 'Kapolei',
    state: 'HI',
    lon: -158.0702,
    lat: 21.3074,
  },
];

export type CnicAssetsTabSeed = CnicHawaiiFacility & {
  threats: string[];
};

export const CNIC_ASSETS_TAB_SEEDS: CnicAssetsTabSeed[] =
  CNIC_HAWAII_FACILITIES.map((facility) => ({
    ...facility,
    threats: [TROPICAL_STORM_THREAT],
  }));

export function formatCnicFacilityLabel(facility: CnicHawaiiFacility): string {
  return `${facility.name} (${facility.city}, ${facility.state})`;
}

export function cnicHawaiiResourceNotesMarker(facilityId: string): string {
  return `${CNIC_HAWAII_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function cnicFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(CNIC_HAWAII_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(CNIC_HAWAII_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isCnicHawaiiResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(CNIC_HAWAII_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromFacility(
  facility: CnicHawaiiFacility
): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: formatCnicFacilityLabel(facility),
    risk:
      `${formatCnicFacilityLabel(facility)} sits inside the tropical storm ` +
      `forecast cone approaching Navy Region Hawaii. Confirm site readiness and report status.`,
    location: [facility.lon, facility.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getCnicRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'Navy Region Hawaii — tropical storm forecast cone',
    description:
      'A tropical storm approaching the Hawaiian Islands places the following CNIC installations inside the forecast cone at elevated risk:',
    threats: CNIC_HAWAII_FACILITIES.map(buildAssetFromFacility),
    responseChecklist: CNIC_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedCnicHawaiiThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === CNIC_HAWAII_THREAT_NOTIFICATION_ID;
}

export function getCnicHawaiiThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: CNIC_HAWAII_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: CNIC_HAWAII_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: CNIC_HAWAII_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'Likely impact to CNIC installations due to Tropical Storm approaching Navy Region Hawaii.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: CNIC_HAWAII_THREAT_PROVENANCE,
  };
}
